import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Hero } from "@/components/Hero";
import { Ribbon } from "@/components/Ribbon";
import { References } from "@/components/References";
import { GalleryGrid } from "@/components/GalleryGrid";
import { ContactBooking } from "@/components/ContactBooking";
import { Footer } from "@/components/Footer";
import { Reveal, Overline } from "@/components/Reveal";
import { SectionBackdrop } from "@/components/SectionBackdrop";
import { api } from "@/lib/api";
import { PLACEHOLDER_GALLERY, POLES } from "@/data/content";
import { useLang, POLE_EN, SERVICE_TITLE_EN } from "@/i18n";

const AUD_KEYS = [
  { t: "aud1.t", x: "aud1.x" },
  { t: "aud2.t", x: "aud2.x" },
  { t: "aud3.t", x: "aud3.x" },
  { t: "aud4.t", x: "aud4.x" },
];

export default function Home() {
  const [items, setItems] = useState(PLACEHOLDER_GALLERY);
  const { t, lang } = useLang();

  useEffect(() => {
    api.get("/gallery")
      .then((res) => { if (res.data.length > 0) setItems(res.data); })
      .catch(() => {});
  }, []);

  return (
    <div>
      <Nav />
      <Hero />
      <Ribbon />

      <section id="le-studio" data-testid="studio-teaser" className="relative py-28 lg:py-36 overflow-hidden">
        <SectionBackdrop images={["/works/work-1.jpg", "/works/work-3.jpg"]} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-end">
          <Reveal className="lg:col-span-7">
            <Overline>{t("ov.studio")}</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight mb-8">
              {t("home.teaserH2a")} <span className="text-gold italic">{t("home.teaserH2b")}</span>
            </h2>
            <p className="text-white/60 leading-relaxed max-w-xl mb-5">
              {t("home.teaserP1")}
            </p>
            <p className="text-white/60 leading-relaxed max-w-xl mb-10">
              {t("home.teaserP2")}
            </p>
            <Link to="/le-studio" data-testid="teaser-link-studio"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-gold group">
              {t("home.teaserLink")}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-5 lg:text-right">
            <p className="font-serif text-7xl lg:text-8xl font-light text-gold leading-none">5 000+</p>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-white/50 mt-3">
              {t("home.stat1")}
            </p>
            <p className="font-serif text-5xl lg:text-6xl font-light text-gold leading-none mt-10">50+</p>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-white/50 mt-3">
              {t("home.stat2")}
            </p>
          </Reveal>
        </div>
      </section>

      <section id="prestations" data-testid="poles-section" className="py-28 lg:py-36 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-16">
            <div>
              <Overline>{t("ov.prestations")}</Overline>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight mb-5">
                {t("home.polesH2")}
              </h2>
              <p className="text-white/60 text-sm leading-relaxed max-w-xl">
                {t("home.polesIntro")}
              </p>
            </div>
            <Link to="/prestations" data-testid="poles-cta"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-gold group w-fit shrink-0">
              {t("home.polesCta")}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
            {POLES.map((pole, i) => (
              <Reveal key={pole.id} delay={i * 0.08}>
                <Link to="/prestations" data-testid={`pole-card-${pole.id}`} className="beam-card block h-full">
                  <div className="beam-inner p-8 lg:p-10 h-full flex flex-col gap-10 group">
                    <div>
                      <span className="font-mono text-xs text-gold/70 tracking-[0.2em]">/0{i + 1}</span>
                      <h3 className="font-serif text-3xl font-light mt-3 mb-2">
                        {lang === "en" ? POLE_EN[pole.id].name : pole.name}
                      </h3>
                      <p className="text-white/50 text-sm">
                        {lang === "en" ? POLE_EN[pole.id].tagline : pole.tagline}
                      </p>
                    </div>
                    <ul className="mt-auto">
                      {pole.prestations.map((pr) => (
                        <li key={pr.slug}
                          className="flex items-center justify-between border-t border-white/10 py-3 text-sm text-white/70 transition-colors duration-300 group-hover:text-white/90">
                          <span>{lang === "en" ? SERVICE_TITLE_EN[pr.title] || pr.title : pr.title}</span>
                          <span className="font-mono text-xs text-white/30">{pr.index}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <References />

      <section id="pour-qui" data-testid="audiences-section" className="relative py-28 lg:py-36 border-t border-white/10 overflow-hidden">
        <SectionBackdrop images={["/works/work-4.jpg", "/works/work-5.jpg"]} opacity={0.2} />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="mb-12">
            <Overline>{t("ov.pourqui")}</Overline>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight">
              {t("home.pourquiH2")}
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10">
            {AUD_KEYS.map((a, i) => (
              <Reveal key={a.t} delay={i * 0.06}>
                <div data-testid={`audience-card-${i}`}
                  className="bg-[#0a0a0a] p-8 lg:p-10 h-full group transition-colors duration-500 hover:bg-[#121212]">
                  <h4 className="font-serif text-xl lg:text-2xl font-light mb-3 transition-colors duration-300 group-hover:text-gold">
                    {t(a.t)}
                  </h4>
                  <p className="text-white/50 text-sm leading-relaxed">{t(a.x)}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="gallery-preview-section" className="py-28 lg:py-36 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14">
            <div>
              <Overline>{t("ov.gallery")}</Overline>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal tracking-tight">
                {t("home.galleryH2")}
              </h2>
            </div>
            <Link to="/galerie" data-testid="gallery-preview-link"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-[0.15em] text-gold group w-fit">
              {t("home.galleryCta")}
              <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
          <GalleryGrid items={items.slice(0, 8)} />
        </div>
      </section>

      <ContactBooking />
      <Footer />
    </div>
  );
}
